import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { ESocialMedia } from '@/store/api/NewSearch/NewSearch.types'
import { useCreateUserDigitalFootprintMutation } from '@/store/api/Users/Users.query'
import type {
  TSocialMediaUser,
  UserInfoResponse,
} from '@/store/api/Users/Users.types'
import { sanitizeSocialMediaUrl } from '@/utils/sanitizeSocialMediaUrl'
import type { prefillSearchFormSchemaTypes } from '@/utils/schemas'
import { prefillSearchFormSchema } from '@/utils/schemas'

import { SocialMediaInputs } from './SocialMediaInputs'

export const SearchForm = ({
  currentData,
}: {
  currentData: UserInfoResponse
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [createUserDigitalFootprint, { isLoading }] =
    useCreateUserDigitalFootprintMutation()

  const form = useForm<prefillSearchFormSchemaTypes>({
    resolver: zodResolver(prefillSearchFormSchema),
    defaultValues: {
      facebookUrl:
        currentData.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.FACEBOOK,
        )?.profileLink || '',
      instagramUrl:
        currentData.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.INSTAGRAM,
        )?.profileLink || '',
      linkedInUrl:
        currentData.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.LINKEDIN,
        )?.profileLink || '',
      tikTokUrl:
        currentData.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.TIKTOK,
        )?.profileLink || '',
      pinterestUrl:
        currentData.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.PINTEREST,
        )?.profileLink || '',
      redditUrl:
        currentData.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.REDDIT,
        )?.profileLink || '',
      twitterUrl:
        currentData.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.TWITTER,
        )?.profileLink || '',
    },
    mode: 'onBlur',
  })

  const { handleSubmit, watch } = form

  // Watch all social media fields to determine if at least one has valid data
  // This fixes the issue where isValid doesn't update properly with defaultValues in onBlur mode
  const socialMediaValues = watch([
    'facebookUrl',
    'instagramUrl',
    'linkedInUrl',
    'tikTokUrl',
    'pinterestUrl',
    'redditUrl',
    'twitterUrl',
  ])

  const hasAtLeastOneSocialMedia = socialMediaValues.some(
    (value) => value && value.trim() !== '',
  )

  const onSubmitForm = handleSubmit(
    async (data: prefillSearchFormSchemaTypes) => {
      const facebookUrl = sanitizeSocialMediaUrl(data.facebookUrl || '')
      const instagramUrl = sanitizeSocialMediaUrl(data.instagramUrl || '')
      const linkedInUrl = sanitizeSocialMediaUrl(data.linkedInUrl || '')
      const tikTokUrl = sanitizeSocialMediaUrl(data.tikTokUrl || '')
      const pinterestUrl = sanitizeSocialMediaUrl(data.pinterestUrl || '')
      const redditUrl = sanitizeSocialMediaUrl(data.redditUrl || '')
      const twitterUrl = sanitizeSocialMediaUrl(data.twitterUrl || '')
      const socialMedias = [
        { name: ESocialMedia.FACEBOOK, profileLink: facebookUrl },
        { name: ESocialMedia.INSTAGRAM, profileLink: instagramUrl },
        { name: ESocialMedia.LINKEDIN, profileLink: linkedInUrl },
        { name: ESocialMedia.TIKTOK, profileLink: tikTokUrl },
        { name: ESocialMedia.PINTEREST, profileLink: pinterestUrl },
        { name: ESocialMedia.REDDIT, profileLink: redditUrl },
        { name: ESocialMedia.TWITTER, profileLink: twitterUrl },
      ].filter((socialMedia) => socialMedia.profileLink.trim() !== '')

      try {
        await createUserDigitalFootprint({
          socialMedias: socialMedias as TSocialMediaUser[],
        }).unwrap()

        navigate(AppRoutes.defaultBehaviorAndTopics)
      } catch (error) {
        toast.error(t('setup.searchForm.errorSettingUp'))
        catchWithSentry(error)
      }
    },
  )

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-8 flex-1 pb-6">
        <div className="flex flex-col gap-3 items-center justify-center">
          <p className="text-[40px] max-md:text-3xl font-medium text-neutralColor text-center">
            {t('setup.searchForm.title')}
          </p>
          <p className="text-2xl font-normal text-neutralColor80 text-center max-md:text-xl">
            {t('setup.searchForm.subtitle')}
          </p>
        </div>

        {/* Social Media Section */}
        <div className="flex flex-col gap-8 w-full bg-baseColorHighlight p-4 rounded-2xl">
          <div className="flex flex-col gap-6 w-full">
            <div className="flex flex-col gap-3">
              <h3 className="text-neutralColor text-xl max-md:text-lg font-medium">
                {t('setup.searchForm.addProfilesTitle')}
              </h3>
              <p className="text-neutralColor80 text-base font-normal">
                {t('setup.searchForm.addProfilesDescription')}
              </p>
            </div>

            <SocialMediaInputs />
          </div>

          <div className="flex flex-row w-full justify-end">
            <Button
              onClick={onSubmitForm}
              disabled={isLoading || !hasAtLeastOneSocialMedia}
              className="font-medium text-base"
            >
              {t('common.continue')}
            </Button>
          </div>
        </div>
      </div>
    </FormProvider>
  )
}
